
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { OptgroupComponent } from '../optgroup/optgroup.component';
import { OptionComponent } from '../option/option.component';

export interface SelectItem<T> {
  id: string | number;
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectOptgroup<T> {
  label: string;
  options: SelectItem<T>[];
}

@Component({
  selector: 'ui-select',
  imports: [],
  templateUrl: './select.component.html',
  styleUrls: ['../../../../../../ui-components/src/lib/_styles/index.scss', '../../../../../../ui-components/src/lib/selects/selects.scss', './select.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent<T> implements ControlValueAccessor, OnInit {
  // Injections
  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;

  // View children
  readonly uiOptgroups = contentChildren(OptgroupComponent);
  readonly uiOptions = contentChildren(OptionComponent, { descendants: true });
  readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton');
  readonly dropdown = viewChild<ElementRef<HTMLDivElement>>('dropdown');
  readonly projectedContentWrapper = viewChild<ElementRef>('projectedContentWrapper');

  // Inputs
  readonly label = input.required<string>();
  readonly options = input<SelectItem<T>[]>();
  readonly optgroups = input<SelectOptgroup<T>[]>();
  readonly emptyMultiselectMessage = input<string>('No results found');
  readonly placeholder = input<string>('Select an option');
  readonly labelPosition = input<'top' | 'left' | 'right' | 'hide'>('top');
  readonly size = input<'default' | 'compact'>('default');
  readonly showCheckbox = input<boolean>(false);
  readonly showSearch = input<boolean>(false);
  readonly filterResultsThroughSearchTerm = input<boolean>(true);
  readonly searchPlaceholder = input<string>('Type to filter options');

  // Outputs
  searchTermChanged = output<string>();

  // Generated attributes
  readonly id = crypto.randomUUID();
  // Accessibility: Unique ID for semantic linking via aria-controls
  readonly dropdownId = `${this.id}-dropdown`;
  // Accessibility: Live region ID for announcing selection changes to screen readers
  readonly liveRegionId = `${this.id}-live`;

  // State
  readonly selectedValue = signal<SelectItem<T> | null>(null);
  readonly isOpen = signal(false);
  // Filter term for searching/filtering options in dropdown
  readonly searchTerm = signal('');
  // Accessibility: Tracks focused option index for keyboard navigation
  readonly focusedIndex = signal(-1);
  // Accessibility: Message announced to screen readers via live region
  readonly liveMessage = signal('');
  protected readonly disabled = signal(false);

  // Keyboard constants
  private readonly KEYS = {
    SPACE: ' ',
    ESCAPE: 'Escape',
    ENTER: 'Enter',
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
  } as const;

  // Reactively parse content children when they change
  readonly parsedOptgroups = computed(() => {
    if (this.options() || this.optgroups()) return [];

    const uiOptgroups = this.uiOptgroups();
    if (uiOptgroups.length) {
      return uiOptgroups.map(group => ({
        label: group.label(),
        options: group
          .uiOptions()
          .filter(opt => opt.value() !== undefined)
          .map(opt => ({
            id: crypto.randomUUID(),
            value: opt.value() as T,
            label: opt.elementRef.nativeElement.textContent?.trim() || '',
            disabled: opt.disabled(),
          })),
      }));
    }

    const uiOpts = this.uiOptions();
    if (uiOpts.length) {
      const options = uiOpts
        .filter(opt => opt.value() !== undefined)
        .map(opt => ({
          id: crypto.randomUUID(),
          value: opt.value() as T,
          label: opt.elementRef.nativeElement.textContent?.trim() || '',
          disabled: opt.disabled(),
        }));
      return [{ label: '', options }];
    }

    return [];
  });

  // Computed values
  private readonly allOptions = computed(() => {
    if (this.optgroups()) return this.optgroups()!.flatMap(g => g.options);
    if (this.parsedOptgroups().length) return this.parsedOptgroups().flatMap(g => g.options);
    return this.options() || [];
  });

  private readonly enabledOptions = computed(() => this.allOptions().filter(option => !option.disabled));

  // Accessibility: Compute ID of currently focused option for aria-activedescendant
  readonly activeDescendantId = computed(() => {
    const index = this.focusedIndex();
    if (index < 0 || !this.isOpen()) return '';
    const options = this.enabledOptions();
    return options[index]?.id ? `option-${options[index].id}` : '';
  });

  readonly ariaLabel = computed(() => {
    const selected = this.selectedValue();
    if (selected) return `${this.label()}, ${selected.label} selected`;
    return `${this.label()}, ${this.placeholder()}`;
  });

  readonly isRequired = computed(() => this.ngControl?.control?.hasValidator(Validators.required) ?? false);

  readonly displayLabel = computed(() => this.selectedValue()?.label || this.placeholder());

  readonly displayOptgroups = computed(() => (this.optgroups() || this.parsedOptgroups()).filter(group => group.label));

  readonly displayOptions = computed(() => {
    if (this.options()) return this.options()!;
    const groups = this.parsedOptgroups();
    return groups.length === 1 && !groups[0].label ? groups[0].options : [];
  });

  // Filter flat options by search term
  readonly filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term || !this.filterResultsThroughSearchTerm()) return this.displayOptions();
    return this.displayOptions().filter(opt => opt.label.toLowerCase().includes(term));
  });

  // Filter grouped options by search term and remove empty groups
  readonly filteredOptgroups = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term || !this.filterResultsThroughSearchTerm()) return this.displayOptgroups();
    return this.displayOptgroups()
      .map(group => ({
        ...group,
        options: group.options.filter(opt => opt.label.toLowerCase().includes(term)),
      }))
      .filter(group => group.options.length > 0);
  });

  readonly hasProjectedContent = computed(() => {
    const wrapper = this.projectedContentWrapper();
    return wrapper ? wrapper.nativeElement?.children?.length > 0 : false;
  });

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // ControlValueAccessor implementation
  private onChange = (value: SelectItem<T> | null) => {};
  private onTouched = () => {};

  writeValue(value: SelectItem<T> | null): void {
    this.selectedValue.set(value);
  }

  registerOnChange(fn: (value: SelectItem<T> | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Dropdown and selection handlers
  // Open dropdown with initial focus
  private openDropdown(): void {
    this.isOpen.set(true);
    this.focusedIndex.set(0);
    setTimeout(() => this.focusDropdownItem(0));
  }

  // Open or close the dropdown menu
  toggleDropdown(): void {
    if (!this.disabled()) {
      const willOpen = !this.isOpen();
      this.isOpen.set(willOpen);
      if (willOpen) {
        this.openDropdown();
      }
    }
  }

  // Accessibility: Close dropdown and return focus to trigger button
  closeDropdown(): void {
    this.isOpen.set(false);
    this.focusedIndex.set(-1);
    // Reset search filter when closing
    this.searchTerm.set('');
    this.triggerButton()?.nativeElement.focus();
  }

  // Accessibility: Move focus to dropdown option at specified index
  private focusDropdownItem(index: number): void {
    const items = this.dropdown()?.nativeElement.querySelectorAll('.option:not(.disabled)');
    if (items && items[index]) {
      (items[index] as HTMLElement).focus();
    }
  }

  // Select an option and close the dropdown
  selectOption(option: SelectItem<T>): void {
    if (option.disabled || this.disabled()) return;
    this.selectedValue.set(option);
    this.onChange(option);
    // Accessibility: Announce selection to screen readers
    this.liveMessage.set(`${option.label} selected`);
    this.closeDropdown();
  }

  // Update search term from input field
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
    this.searchTermChanged.emit(this.searchTerm());
  }

  // Event handlers
  handleOnBlur(): void {
    this.onTouched();
  }

  // Accessibility: Navigate through options with arrow keys
  private navigateOptions(direction: 'up' | 'down', items: NodeListOf<Element>): void {
    const current = this.focusedIndex();
    const next = direction === 'down' ? Math.min(current + 1, items.length - 1) : Math.max(current - 1, 0);
    this.focusedIndex.set(next);
    this.focusDropdownItem(next);
    this.announceOption(next, items.length);
  }

  // Accessibility: Handle keyboard interaction on trigger button (Space, Arrow keys open dropdown)
  handleTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.key === this.KEYS.SPACE || event.key === this.KEYS.ARROW_DOWN || event.key === this.KEYS.ARROW_UP) {
      event.preventDefault();
      if (!this.isOpen()) {
        this.openDropdown();
      } else if (event.key === this.KEYS.SPACE) {
        this.closeDropdown();
      }
    }
  }

  // Accessibility: Handle keyboard navigation in dropdown (Escape closes, Enter/Space selects, Arrows navigate)
  handleDropdownKeydown(event: KeyboardEvent, option: SelectItem<T>): void {
    const items = this.dropdown()?.nativeElement.querySelectorAll('.option:not(.disabled)');

    if (event.key === this.KEYS.ESCAPE) {
      event.preventDefault();
      this.closeDropdown();
    } else if (event.key === this.KEYS.ENTER || event.key === this.KEYS.SPACE) {
      event.preventDefault();
      this.selectOption(option);
    } else if (event.key === this.KEYS.ARROW_DOWN) {
      event.preventDefault();
      this.navigateOptions('down', items!);
    } else if (event.key === this.KEYS.ARROW_UP) {
      event.preventDefault();
      this.navigateOptions('up', items!);
    }
  }

  // Accessibility: Announce option label and position (e.g., "Blue Whale, 2 of 5")
  private announceOption(index: number, total: number): void {
    const options = this.enabledOptions();
    const option = options[index];
    if (option) {
      this.liveMessage.set(`${option.label}, ${index + 1} of ${total}`);
    }
  }

  // Accessibility: Close dropdown when clicking outside component
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }
}
